import uniq from 'lodash/uniq';

import IncidentByUrls from 'redpoints-model-js/model/system/IncidentByUrls';

import HttpStatus from 'redpoints-connector-common/enumeration/HttpStatus';
import ApiResponse from 'redpoints-connector-common/model/ApiResponse';
import FacilitatorValidationTaskController from 'redpoints-restapi-connector/controller/FacilitatorValidationTaskController';
import IncidentByUrlCreationController from 'redpoints-restapi-connector/controller/IncidentByUrlCreationController';

import { mergeContext } from 'redpoints-front-common/authentication';
import { isValidUrl } from 'redpoints-front-common/helpers';
import { OPERATIONS } from 'redpoints-front-common/services';

import { translate } from '../helpers/languageHelper';

const TYPES = {
  MARK_AS_DONE: 'markAsDone',
  PROCESS_CONTENT_LINKS: 'processContentLinks',
};
export const TASK_KEY = 'task';

const getResponse = response => {
  if (!allResponsesHasTheExpectedHTTPStatus(response)) {
    return Object.assign(new ApiResponse(), {
      error: translate('common.entitiesNotProcessed'),
      response,
      status: HttpStatus.CALL_FAILURE.value,
    });
  }

  return Object.assign(new ApiResponse(), {
    response,
    status: HttpStatus.OK.value,
  });
};

export const getProcessedEntities = (entitiesToProcess, resolvedPromises) =>
  entitiesToProcess.map((item, index) => {
    const { status, response, reason, error } = resolvedPromises[index];
    const entity = { ...item, status, reason };

    if (response) {
      entity.value = response;
    }
    if (error) {
      entity.error = error;
    }

    return entity;
  });

const allResolvedContentPromisesAreOk = resolvedPromises =>
  resolvedPromises.length === 0 ||
  Object.values(resolvedPromises).every(
    ({ status }) => status === HttpStatus.CREATED.value
  );

const allResponsesHasTheExpectedHTTPStatus = processedEntities =>
  Object.values(processedEntities).every(
    ({ status }) =>
      status === HttpStatus.CREATED.value ||
      status === HttpStatus.NO_CONTENT.value
  );

const getKeyPromiseStructure = ({
  context,
  contentLink,
  linkTaskRequest,
  type,
}) => {
  const {
    assetId,
    contractId,
    customerId,
    linkId,
    protectionType,
    url,
  } = linkTaskRequest;

  const newContext = mergeContext(context, {
    customerId,
    contractId,
  });

  if (type === TYPES.MARK_AS_DONE) {
    return {
      key: TASK_KEY,
      operation: OPERATIONS.UPDATE,
      value: FacilitatorValidationTaskController.doneFacilitatorValidation.bind(
        null,
        newContext,
        customerId,
        linkId
      ),
    };
  }

  if (type === TYPES.PROCESS_CONTENT_LINKS) {
    if (isValidUrl(contentLink)) {
      const full = false;
      const incidentByUrls = Object.assign(new IncidentByUrls(), {
        detectionLinkUrl: url,
        incidentLinkUrl: contentLink,
        protectionType,
      });

      return {
        key: contentLink,
        operation: OPERATIONS.CREATE,
        value: IncidentByUrlCreationController.createByUrls.bind(
          null,
          newContext,
          assetId,
          full,
          incidentByUrls
        ),
      };
    }

    const error = new Error(translate('common.invalidUrl'));
    error.status = HttpStatus.NOT_ACCEPTABLE.value;

    return {
      key: contentLink,
      operation: OPERATIONS.CREATE,
      error,
    };
  }
};

const getContentLinkEntitiesToProcess = (
  context,
  contentLinks,
  linkTaskRequest
) => {
  const contentLinksArray = uniq(
    contentLinks
      ?.split('\n')
      .map(s => s.trim())
      .filter(Boolean)
  );

  return contentLinksArray.map(contentLink =>
    getKeyPromiseStructure({
      context,
      contentLink,
      linkTaskRequest,
      type: TYPES.PROCESS_CONTENT_LINKS,
    })
  );
};

const getTaskEntityToProcess = (context, linkTaskRequest) =>
  getKeyPromiseStructure({
    context,
    linkTaskRequest,
    type: TYPES.MARK_AS_DONE,
  });

export default class FacilitatorValidationService {
  static async save(context, contentLinks, linkTaskRequest) {
    const contentEntitiesToProcess = getContentLinkEntitiesToProcess(
      context,
      contentLinks,
      linkTaskRequest
    );

    const taskEntityToProcess = getTaskEntityToProcess(
      context,
      linkTaskRequest
    );

    if (![...contentEntitiesToProcess, taskEntityToProcess].length > 0) {
      return getResponse([]);
    }

    let resolvedPromises = [];
    let entitiesToProcess = [];
    let resolvedContentPromises = [];

    if (contentEntitiesToProcess.length > 0) {
      for (const contentEntityToProcess of contentEntitiesToProcess) {
        const isValidUrlContentEntity = contentEntityToProcess?.value;

        if (isValidUrlContentEntity) {
          resolvedContentPromises.push(await contentEntityToProcess.value());
        } else {
          resolvedContentPromises.push(contentEntityToProcess?.error);
        }
      }
      entitiesToProcess = Array.from(contentEntitiesToProcess);
      resolvedPromises = Array.from(resolvedContentPromises);
    }

    if (allResolvedContentPromisesAreOk(resolvedContentPromises)) {
      resolvedPromises.push(await taskEntityToProcess.value());
      entitiesToProcess.push(taskEntityToProcess);
    }

    const processedEntities = getProcessedEntities(
      entitiesToProcess,
      resolvedPromises
    );

    return getResponse(processedEntities);
  }
}
